import { useCallback, useEffect, useRef, useState } from "react"
import { useScope } from "../../../container/SelectProjectCompanyContainer"
import Send from "../../communication"
import { customToast } from "../../../components/Toast"
import { getStore } from "../../redux/setup"
import { getIds } from "../getIds"

export const useFormProject = (scopeId, defaultScopeType = "project") => {
  const { scope, scopeType, projectsByScope } = useScope()
  const [forms, setForms] = useState([])
  const [selectedFormId, setSelectedFormId] = useState(null)
  const [selectedStepId, setSelectedStepId] = useState(null)
  const [steps, setSteps] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState([])
  const [filteredGroupsWhenNoGroupIsSelected, setFilteredGroupsWhenNoGroupIsSelected] = useState([]);
  const [groups, setGroups] = useState([])
  const [filteredGroups, setFilteredGroups] = useState([])
  const [groupQuestions, setGroupQuestions] = useState([])
  const [groupQuestionsWithConditional, setGroupQuestionsWithConditional] = useState([])
  const [reload, setReload] = useState(false)

  useEffect(() => {
    if (scope && scope?.id) {
      const newScope = {
        id: scopeId ? scopeId : scope.id,
        type: defaultScopeType ? defaultScopeType : scopeType,
      }

      Send.get(`/forms-projects/by-project/${newScope?.id}`, `scopeType=${newScope.type}`)
        .then((response) => {
          setForms(
            Array.isArray(response) ? response?.sort((a, b) => a.title.localeCompare(b.title)) : [],
          )
          setSelectedFormId(null)
          setSteps([])
        })
        .catch((error) => {
          customToast("Erro ao buscar formulários.", "error")
          console.log("Erro ao buscar formulários. ", error)
        })
    }

    return () => setForms([])
  }, [scopeId, scope, reload])

  useEffect(() => {
    if (selectedFormId) {
      Send.get(
        `/forms-wizards-groups/form/${Array.isArray(selectedFormId)
          ? selectedFormId.map((row) => row.value).join(",")
          : selectedFormId
        }`,
        `projects=${getIds(projectsByScope, "id")}`,
      )
        .then((result) => {
          setSteps(
            result.steps?.map(({ stepId, stepTitle }) => ({ value: stepId, label: stepTitle })),
          )
          setGroups(result.groups)
        })
        .catch((error) => {
          customToast("Erro ao buscar etapas do formulário.", "error")
          console.log("Erro ao buscar etapas do formulário.", error)
        })
    }
  }, [selectedFormId])

  useEffect(() => {
    if (selectedStepId) {
      if (Array.isArray(selectedStepId)) {
        const filtered = groups.filter((item) =>
          selectedStepId.some((step) => step.value === item.stepId),
        )
        setFilteredGroups(filtered)
      } else {
        const filtered = groups.filter((item) => item.stepId === selectedStepId.value)
        setFilteredGroups(filtered)
      }
    } else {
      setFilteredGroups(groups)
    }
  }, [selectedStepId, groups])

  useEffect(() => {
    if (selectedGroupId && selectedGroupId?.length > 0) {
      Send.get(
        `/forms-wizards-groups/questions/${selectedGroupId[0]?.value || selectedGroupId}`,
        `questions=${Array.isArray(selectedGroupId)
          ? selectedGroupId.map((row) => `'${row?.value}'`).join(",")
          : `'${selectedGroupId}'`
        }`,
      )
        .then((result) => {
          const groupQuestions =
            result && result.length > 0
              ? result?.filter((item) => item?.isConditional === 0 || !item?.isConditional)
              : []
          setGroupQuestions(groupQuestions)
          setGroupQuestionsWithConditional(result)
        })
        .catch((error) => {
          customToast("Erro ao buscar questões da etapa.", "error")
          console.log("Erro ao buscar questões da etapa.", error)
        })
    } else {
      setGroupQuestions([])
      setGroupQuestionsWithConditional([])
    }
  }, [selectedGroupId])

  useEffect(() => {
    if (selectedGroupId?.length === 0 && selectedStepId && groups?.length > 0) {
      const filtered = groups
        .filter(group =>
          selectedStepId.some(step => step.value === group.stepId)
        );

      setFilteredGroupsWhenNoGroupIsSelected(filtered);
    } else {
      setFilteredGroupsWhenNoGroupIsSelected([]);
    }
  }, [selectedGroupId, selectedStepId, groups]);

  return {
    forms,
    setReload,
    setSelectedFormId,
    selectedFormId,
    steps,
    setSelectedStepId,
    selectedStepId,
    setSelectedGroupId,
    selectedGroupId: filteredGroupsWhenNoGroupIsSelected?.length > 0 ? filteredGroupsWhenNoGroupIsSelected : selectedGroupId,
    groups: filteredGroups,
    groupQuestions,
    groupQuestionsWithConditional,
  }
}

const getCacheEntryWithSubstring = (obj, substring) => {
  if (obj && Object.keys(obj).length > 0) {
    for (const key of Object.keys(obj)) {
      if (key.includes(substring)) {
        return obj[key]; // retorna o objeto inteiro { data, cachedAt }
      }
    }
  }
  return null;
};

export const useOfflineFormProject = (scopeId, defaultScopeType = "project") => {
  const { scope, scopeType } = useScope();
  const [isLoading, setIsLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [reload, setReload] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [error, setError] = useState(null);
  const cache = getStore().getState().cache;
  const hasCheckedCache = useRef(false);

  const cacheEntry = getCacheEntryWithSubstring(
    cache,
    `forms-projects/by-project/${scope?.id}?scopeType=${scopeType}`,
  );

  const hasCachedData = cacheEntry?.data || null;
  const cachedAt = cache?.lastCachedAt || null;

  const fetchForms = useCallback(async () => {
    if (scope && scope.id) {
      setIsLoading(true);
      setReload(false);
      setError(null);
      try {
        const newScope = {
          id: scopeId || scope.id,
          type: defaultScopeType || scopeType,
        };

        const formsResponse = await Send.get(
          `/forms-projects/by-project/${newScope.id}`,
          `scopeType=${newScope.type}`,
        );

        const fetchedForms = Array.isArray(formsResponse) ? formsResponse : [];

        const allSteps = await Promise.all(
          fetchedForms.map(async (form) => {
            const stepsResponse = await Send.get(
              `/assistance-link-form/questions/offline/form/${form.id}/project/${scope.id}`,
              `scopeType=${newScope.type}`,
            );
            return { ...form, steps: stepsResponse };
          }),
        );

        setForms(allSteps);
      } catch (error) {
        setError("Erro ao buscar dados");
      } finally {
        setIsLoading(false);
      }
    }
  }, [scope, scopeId, defaultScopeType, scopeType]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (reload || !hasCheckedCache.current) {
          hasCheckedCache.current = true;

          if (!hasCachedData?.length || reload) {
            await fetchForms();
          } else if (hasCachedData.length > 0 && !forms.length) {
            setForms(hasCachedData);
          }
        } else if (!hasCachedData?.length) {
          setForms([]);
        }
      } catch (error) {
        setError("Ocorreu um erro inesperado.");
        console.error("Erro ao carregar dados:", error);
      }
    };

    loadData();
  }, [fetchForms, hasCachedData, reload]);

  const reloading = useCallback(() => setReload((prev) => !prev), []);

  return {
    forms,
    cachedAt,
    setSelectedFormId,
    selectedFormId,
    isLoading,
    reload: reloading,
    error,
  };
};